<template>
  <div class="health-check">
    <div class="page-container">
      <div class="header">
        <h2>股票体检</h2>
      </div>
      
      <div class="search-section">
        <div class="search-form">
          <input 
            type="text" 
            v-model.trim="inputStockCode" 
            @keyup.enter="doInputStockCode" 
            @focus="selectAllOnFocus"
            placeholder="输入股票代码 (e.g., 000123)"
            class="stock-input"
            ref="stockInput"
          >
          <button @click="doInputStockCode" :disabled="loading" class="check-btn">
            {{ loading ? '查询中...' : '体检' }}
          </button>
        </div>
        
      </div>
        
      <!-- 体检历史记录 -->
      <div class="history-section" v-if="false && checkHistory.length > 0">
        <span class="history-label">体检历史：</span>
        <span class="history-list">
          <a 
            v-for="(code, index) in checkHistory" 
            :key="index" 
            class="history-item"
            @click="checkHistoricalStock(code)"
          >
            {{ code }}
          </a>
        </span>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-else>
        <!-- 第0部分：显示股票名称、code等基本信息 -->
        <div class="stock-basic-info" v-if="basicData || loadingStates.basic">
          <div class="basic-info-card">
            <div class="left-group">
              <div class="stock-name">
                <span class="stock-name-text">{{ basicData ? basicData.stockName : '正在加载...' }}</span>
                <span class="stock-code-text" v-if="basicData">({{ basicData.stockCode }})</span>
              </div>
              <div class="price-info" v-if="basicData">
                <span class="price">{{ basicData.price.toFixed(getPricePrecision(basicData.stockCode)) }}</span>
                <span class="change">
                  {{ basicData.changePercent }}
                </span>
                <button 
                  v-if="false"
                  class="add-to-monitor-btn" 
                  @click="handleAddToMonitor"
                  :disabled="isStockMonitored()"
                >
                  {{ isStockMonitored() ? '已监控' : '添加到监控' }}
                </button>
              </div>
              <div class="price-info" v-else>
                <span class="loading-text">等待中...</span>
              </div>
            </div>
            <div class="time-info">{{ getCurrentTimeFormatted() }}</div>
          </div>
        </div>

        <!-- 第1部分：显示technical技术指标 -->
        <div class="section technical-section" v-if="technicalData || loadingStates.technical">
          <h3>（1）技术面
            <span class="section-score" :class="technicalData ? getHealehTotalScoreClass(technicalData.score, 42) : ''">
              <span v-if="technicalData">{{ technicalData.score }}/42</span>
              <span v-else>等待中...</span>
            </span>
          </h3>
          <div class="technical-table-container" v-if="technicalData">
            <table class="technical-table">
              <thead>
                <tr>
                  <th v-for="(item, index) in technicalData.header" :key="index">{{ item }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(scale, scale_index) in technicalData.technical" :key="scale_index">
                  <td v-for="(item, item_index) in scale" :key="item_index">
                    <div v-if="item.title == 'scaleTitle' || item.title == 'score' || item.title == 'td'">
                      <span v-if="item.title == 'score'" :class="getHealehTotalScoreClass(item.value, 14)">{{ item.value }}/14</span>
                      <span v-else>{{ item.value }}</span>
                    </div>
                    <div v-else>
                      <span v-if="item.title == 'MACD' || item.title == 'DIFF' || item.title == 'KDJ(D)'" :class="getTechnicalValueColorClass(parseFloat(item.positive))">{{ parseFloat(item.value).toFixed(3) }}</span>
                      <span v-else :class="getTechnicalValueColorClass(parseFloat(item.positive))">{{ parseFloat(item.value).toFixed(getPricePrecision(basicData.stockCode)) }}</span>
                      <span :class="getTechnicalValueColorClass(parseFloat(item.up))"> {{ getTechnicalArrow(item.up) }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="loading-placeholder">
            <p>正在加载技术面数据...</p>
          </div>
        </div>

        <!-- 第2部分：显示fundamentals基本面 -->
        <div class="section fundamentals-section" v-if="(fundamentalsData || loadingStates.fundamentals) && isNormalStock(realStockCode)">
          <h3>（2）基本面
            <span class="section-score" :class="fundamentalsData ? getHealehTotalScoreClass(fundamentalsData.score, 5) : ''">
              <span v-if="fundamentalsData">{{ fundamentalsData.score }}/5</span>
              <span v-else>等待中...</span>
            </span>
          </h3>
          <div class="fundamentals-grid" v-if="fundamentalsData">
            <div class="metric-card">
              <div class="metric-item">
                <span class="label">上市日期:</span>
                <span>{{ fundamentalsData.listingDate }}</span>
              </div>
              <div class="metric-item">
                <span class="label">静态市盈率:</span>
                <span>{{ fundamentalsData.spe.toFixed(2) }}</span>
              </div>
              <div class="metric-item">
                <span class="label">TTM市盈率:</span>
                <span>{{ fundamentalsData.ttmpe.toFixed(2) }}</span>
              </div>
              <!-- <div class="metric-item">
                <span class="label">动态市盈率:</span>
                <span>{{ fundamentalsData.dpr.toFixed(2) }}</span>
              </div> -->
            </div>
            <div class="metric-card">
              <div class="metric-item">
                <span class="label">净资产收益率:</span>
                <span>
                  {{ fundamentalsData.roe.toFixed(2) }}%
                </span>
              </div>
              <div class="metric-item">
                <span class="label">总市值:</span>
                <span v-if="!basicData">计算中……</span>
                <span v-else>{{ formatFinancialNumber(parseInt(fundamentalsData.tms * basicData.price)) }}</span>
                <!-- <span>
                  {{ fundamentalsData.tmc.toFixed(2) }}亿
                </span> -->
              </div>
              <div class="metric-item">
                <span class="label">流通市值:</span>
                <span v-if="!basicData">计算中……</span>
                <span v-else>{{ formatFinancialNumber(parseInt(fundamentalsData.cms * basicData.price)) }}</span>
                <!-- <span>
                  {{ fundamentalsData.cmc.toFixed(2) }}亿
                </span> -->
              </div>
            </div>
            <div class="metric-card">
              <div class="metric-item">
                <span class="label">净利润:</span>
                <span :class="getDirectionClass(fundamentalsData.np)">
                  {{ fundamentalsData.np.toFixed(2) }}亿
                  <span v-if="fundamentalsData.period" class="period-circle">{{ fundamentalsData.period }}</span>
                </span>
              </div>
              <div class="metric-item">
                <span class="label">净利润增长率:</span>
                <span :class="getDirectionClass(fundamentalsData.npgr)">
                  {{ fundamentalsData.npgr.toFixed(2) }}%
                </span>
              </div>
              <div class="metric-item">
                <span class="label">主营增长率:</span>
                <span :class="getDirectionClass(fundamentalsData.gr)">
                  {{ fundamentalsData.gr.toFixed(2) }}%
                </span>
              </div>
            </div>
          </div>
          <div v-else class="loading-placeholder">
            <p>正在加载基本面数据...</p>
          </div>
        </div>

        <!-- 第3部分：显示holder股东信息 -->
        <div class="section holder-section" v-if="(holderData || loadingStates.holder) && isNormalStock(realStockCode)">
          <h3>（3）股东数
            <span class="section-score score-value" v-if="holderData" :class="getRankingScoreClass(parseFloat(holderData.score))">
              <span v-if="holderData"> {{ holderData.score }}/5</span>
              <span v-else>等待中...</span>
            </span>
            <span v-if="holderData" :class="getRankingColor(holderData.platePos, holderData.plateCount)">(行业内排名{{ holderData.platePos }}/{{ holderData.plateCount }})</span>
          </h3>
          <div class="fundamentals-grid" v-if="holderData">
            <div class="metric-card">
              <div class="metric-item">
                <span class="label">最近股东数:</span>
                <span class="value">{{ formatNumber(holderData.newestHolderNum) }} ({{ holderData.newestHolderDate }})</span>
              </div>
              <div class="metric-item">
                <span class="label">户均市值:</span>
                <span v-if="!basicData || !fundamentalsData">计算中……</span>
                <span v-else :class="getAverageCapColor(fundamentalsData.cms * basicData.price / holderData.newestHolderNum)">{{ formatFinancialNumber(parseInt(fundamentalsData.cms * basicData.price / holderData.newestHolderNum)) }}</span>
              </div>
            </div>
            <div class="metric-card">
              <div class="metric-item">
                <span class="label">最少股东:</span>
                <span class="value">{{ formatNumber(holderData.lowestHolderNum) }} ({{ holderData.lowestHolderDate }})</span>
              </div>
              <div class="metric-item">
                <router-link :to="{ name: 'SingleStockHolder', params: { stockCode: basicData ? basicData.stockCode : '', yearLimit: 5 } }" class="stock-link">查看股东数详情</router-link>
              </div>
            </div>
            <div class="metric-card">
              <div class="metric-item">
                <span class="label">最多股东:</span>
                <span class="value">{{ formatNumber(holderData.highestHolderNum) }} ({{ holderData.highestHolderDate }})</span>
              </div>
              <div class="metric-item">
                <span class="label">所属行业：</span>
                <router-link :to="{ name: 'IndustryPlateHolders', params: { plateId: holderData.plateId, yearLimit: 5 } }" class="stock-link">{{ holderData.plateName }}</router-link>
              </div>
            </div>
          </div>
          <div v-else class="loading-placeholder">
            <p>正在加载股东信息数据...</p>
          </div>
        </div>

        <!-- 第4部分：显示other其他信息 -->
        <div class="section other-section" v-if="(otherData || loadingStates.other) && isNormalStock(realStockCode)">
          <h3>（4）消息面
            <span class="section-score" :class="otherData ? getHealthOtherScoreClass(otherData.score) : ''">
              <span v-if="otherData">{{ otherData.score }}/5</span>
              <span v-else>等待中...</span>
            </span>
          </h3>
          <div class="stock-plan-list" v-if="otherData">
            <div v-for="(item, index) in otherData.plan" :key="index">
              <div class="stock-plan" v-if="item.type == 'list'">
                <div class="stock-plan-name">
                  <span class="label">{{ item.name }}：</span>
                </div>
                <div class="stock-notice-list" v-if="item.notices">
                  <div class="stock-notice" v-for="(notice, notice_index) in item.notices" :key="notice_index">
                    <span class="notice-title" v-if="!notice.url">{{ notice.title }}</span>
                    <span class="notice-title" v-else>
                      <a :href="notice.url" target="_blank" class="stock-link">{{ notice.title }}</a>
                    </span>
                    <span class="notice-date">{{ notice.notice_date }}</span>
                    <!-- <span v-if="index == 0" :class="getFinanceDateDiffColorClass(notice)" class="notice-datediff">{{ getDateDiff(notice) }}</span>
                    <span v-else :class="getNoticeColorClass(notice.positive)" class="notice-datediff">{{ getDateDiff(notice) }}</span> -->
                    <!-- 2026-01-30: 不知道为什么上面要做一个特殊处理，先去掉，下次碰到了再加上注释 -->
                    <span :class="getNoticeColorClass(notice.positive)" class="notice-datediff">{{ getDateDiff(notice) }}</span>
                  </div>
                </div>
                <div class="stock-notice-list" v-else>
                  <span class="notice-date positive">无</span>
                </div>
              </div>
              <div class="stock-plan" v-else>
                <div class="stock-plan-name">
                  <span class="label">{{ item.name }}：</span>
                  <!-- <span class="value" v-if="item.date">{{ item.date }}</span>
                  <span class="value positive" v-else>无</span> -->
                </div>
                <div class="stock-notice" v-if="item.date">
                  <span class="notice-date">{{ item.date }}</span>
                  <span class="notice-datediff">
                    <span class="label">距今：</span>
                    <span :class="getHealthOtherScoreClass(item.score)">{{ item.dateDiff }}</span>
                    <span> 天</span>
                  </span>
                </div>
                <div class="stock-plan-date" v-else>
                  <span class="notice-date positive">{{ item.state }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="loading-placeholder">
            <p>正在加载其他信息数据...</p>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import mockApi from '@/api/mock';
import { uniformStockCode } from '../api/mock';
import { formatNumber, formatFinancialNumber, getRankingScoreClass, getPricePrecision } from '@/utils/utils';
import { addToMonitor, isValidStockCode } from '@/utils/stockUtils';
import '../assets/common-styles.css';
  
  export default {
  name: 'HealthCheck',
  props: {
    stockCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      inputStockCode: '',
      realStockCode: '',
      realMarket: 'sh',
      // 将stockData分解为5个部分，分别存储不同类型的健康检查数据
      basicData: null,
      technicalData: null,
      fundamentalsData: null,
      holderData: null,
      otherData: null,
      // 监控列表数据
      monitoredStocks: [],
      // 历史记录数据，不包括当前代码
      checkHistory: [],
      // 添加各个部分的加载状态
      loadingStates: {
        basic: false,
        technical: false,
        fundamentals: false,
        holder: false,
        other: false
      },
      // 总体加载状态
      loading: false,
      error: null
    };
  },
  watch: {
    realStockCode(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$nextTick(() => {
          this.checkStock();
        });
      }
    },
    // 监听路由参数变化
    '$route'(to, from) {
      if (to.name !== 'HealthCheck') {
        return;
      }
      console.log("HealthCheck: route change from ", from, " to ", to);
      // 处理stockCode参数变化
      this.realStockCode = to.params.stockCode;
    }
  },
  async mounted() {
    // 初始化监控列表数据
    this.loadMonitoredStocks();
    
    // 如果有传入的stockCode参数，则自动分析
    console.log("HealthCheck mounted, stockCode: ", this.stockCode);
    if (this.stockCode) {
      this.realStockCode = this.stockCode;
    }
    
    // 加载历史记录
    this.loadCheckHistory();
    
    // 页面加载完成后自动聚焦到输入框
    this.$nextTick(() => {
      if (this.$refs.stockInput) {
        this.$refs.stockInput.focus();
      }
    });
  },
  activated() {
    // 当组件被激活时（如从其他页面切换回来），聚焦到输入框
    this.$nextTick(() => {
      if (this.$refs.stockInput) {
        this.$refs.stockInput.focus();
      }
    });
  },
  computed: {
  },
  methods: {
    isNormalStock(stockCode) {
      const fullStockCode = uniformStockCode(stockCode);
      const shortStockCode = fullStockCode.substring(2);
      const market = fullStockCode.substring(0, 2);
      if (this.realMarket == 'zs' || market == 'zs') {
        return false;
      }
      if (market != 'sh' && market != 'sz') {
        return false;
      }
      if (fullStockCode == 'sh000001') {
        return false;
      }
      return !shortStockCode.startsWith('11')
       && !shortStockCode.startsWith('12')
       && !shortStockCode.startsWith('15')
       && !shortStockCode.startsWith('16')
       && !shortStockCode.startsWith('51')
       && !shortStockCode.startsWith('58')
       && !shortStockCode.startsWith('399');   // 深交所指数
    },
    doInputStockCode() {
      console.log("HealthCheck doInputStockCode, inputStockCode: ", this.inputStockCode);
      if (this.inputStockCode.trim()) {
        // 验证股票代码是否合法
        if (!isValidStockCode(this.inputStockCode)) {
          alert("请输入合法的股票代码！\n\n支持格式：\n- 5位或6位纯数字 (如: 000001, 12345)\n- 带前缀的代码 (如: sz000001, sh600000, hk00700)");
          return;
        }
        
        console.log("HealthCheck doInputStockCode, inputStockCode trimmed: ", this.inputStockCode.trim(), this.$router);
        if (this.$router) {
          this.$router.replace({
            name: 'HealthCheck',
            params: { stockCode: this.inputStockCode }
          }).catch(err => {
            // 忽略重复导航的错误
            if (err.name !== 'NavigationDuplicated') {
              console.error('Routing error:', err);
            }
          });
        }
      }
    },
    getRankingScoreClass,
    selectAllOnFocus(event) {
      // 在下一个tick中选中所有文本，确保DOM已更新
      this.$nextTick(() => {
        event.target.select();
      });
    },
    async checkStock() {
      // 重置所有数据和加载状态
      this.basicData = null;
      this.technicalData = null;
      this.fundamentalsData = null;
      this.holderData = null;
      this.otherData = null;

      // 添加到历史记录
      // this.addToCheckHistory(this.realStockCode.trim());
      
      
      // 重置所有加载状态
      Object.keys(this.loadingStates).forEach(key => {
        this.loadingStates[key] = true;
      });
      
      if (!this.realStockCode || !this.realStockCode.trim()) {
        this.inputStockCode = '';
        // 重置加载状态
        Object.keys(this.loadingStates).forEach(key => {
          this.loadingStates[key] = false;
        });
        return;
      }

      this.error = null;
 
      try {
        const fullStockCode = uniformStockCode(this.realStockCode);
        const shortStockCode = fullStockCode.substring(2);
        this.realMarket = fullStockCode.substring(0, 2);
        const isNormalStock = this.isNormalStock(fullStockCode);
        console.log("HealthCheck checkStock, fullStockCode: ", fullStockCode, " shortStockCode: ", shortStockCode, " realMarket: ", this.realMarket, "isNormalStock: ", isNormalStock);
        
        // 发起5个独立的异步请求，数据获取后立即显示
        this.fetchBasicData(shortStockCode, this.realMarket);
        this.fetchTechnicalData(shortStockCode, this.realMarket);
        if (this.isNormalStock(fullStockCode)) {
          this.fetchFundamentalsData(shortStockCode, this.realMarket);
          this.fetchHolderData(shortStockCode, this.realMarket);
          this.fetchOtherData(shortStockCode, this.realMarket);
        }
      } catch (error) {
        this.error = '查询失败，请检查股票代码是否正确';
        console.error('Stock health check error:', error);
        // 重置所有加载状态
        Object.keys(this.loadingStates).forEach(key => {
          this.loadingStates[key] = false;
        });
      }
    },
    
    // 异步获取基本信息
    async fetchBasicData(stockCode, market) {
      try {
        const response = await mockApi.getStockHealthCheck(stockCode, market, 'basic');
        if (response.success) {
          this.basicData = response.info;
        }
        else {
          this.error = '获取股票信息失败，请检查输入代码是否正确';
        }
        this.loadingStates.basic = false;
      } catch (error) {
        console.error('获取基本信息失败:', error);
        this.loadingStates.basic = false;
      }
    },
    
    // 异步获取技术面数据
    async fetchTechnicalData(stockCode, market) {
      try {
        const response = await mockApi.getStockHealthCheck(stockCode, market, 'technical');
        if (response.success) {
          this.technicalData = response.info.technical;
          console.log("technicalData: ", this.technicalData);
        }
        this.loadingStates.technical = false;
      } catch (error) {
        console.error('获取技术面数据失败:', error);
        this.loadingStates.technical = false;
      }
    },
    
    // 异步获取基本面数据
    async fetchFundamentalsData(stockCode, market) {
      try {
        const response = await mockApi.getStockHealthCheck(stockCode, market, 'fundamentals');
        if (response.success) {
          this.fundamentalsData = response.info.fundamentals;
        }
        this.loadingStates.fundamentals = false;
      } catch (error) {
        console.error('获取基本面数据失败:', error);
        this.loadingStates.fundamentals = false;
      }
    },
    
    // 异步获取股东信息
    async fetchHolderData(stockCode, market) {
      try {
        const response = await mockApi.getStockHealthCheck(stockCode, market, 'holder');
        if (response.success) {
          this.holderData = response.info.holder;
        }
        this.loadingStates.holder = false;
      } catch (error) {
        console.error('获取股东信息失败:', error);
        this.loadingStates.holder = false;
      }
    },
    
    // 异步获取其他信息
    async fetchOtherData(stockCode, market) {
      try {
        const response = await mockApi.getStockHealthCheck(stockCode, market, 'other');
        if (response.success) {
          this.otherData = response.info.other;
        }
        this.loadingStates.other = false;
      } catch (error) {
        console.error('获取其他信息失败:', error);
        this.loadingStates.other = false;
      }
    },
    formatPercentage(percentage) {
      if (!percentage) return '0.00%';
      const num = parseFloat(percentage);
      return `${num > 0 ? '+' : ''}${(num * 100).toFixed(2)}%`;
    },

    formatStockPlanDate(plan) {
      if (!plan) return '';
      if (!plan.startDate) {
        return "无";
      }
      
      // 根据不同类型格式化日期
      switch (plan.type) {
        case 'year':
          // 只有开始日期，格式：YYYY-MM-DD
          return plan.startDate + " 年报";
        case 'range':
          // 有开始和结束日期，格式：YYYY-MM-DD 至 YYYY-MM-DD
          return `${plan.startDate} 至 ${plan.endDate}`;
        case 'normal':
        default:
          // 只有开始日期，格式：YYYY-MM-DD
          return plan.startDate;
      }
    },
    calculatePlanDate(plan) {
      if (!plan || !plan.startDate) return 0;
      
      // 解析 startDate 字符串为日期对象
      const startDate = new Date(plan.startDate);
      
      // 获取今天的日期
      const today = new Date();
      
      // 设置时间为00:00:00，避免时间部分影响天数计算
      startDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      // 计算两个日期之间的毫秒差
      const timeDiff = startDate.getTime() - today.getTime();
      
      // 将毫秒差转换为天数差
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      return daysDiff;
    },

    getTechnicalValueColorClass(positive) {
      // 根据API返回的颜色代码确定样式类
      // 1: 红色, -1: 绿色, 0: 无颜色
      if (positive === -1) return 'negative';    // 绿色
      if (positive === 1) return 'positive';   // 红色
      return '';
    },

    getDirectionClass(value) {
      if (!value) return '';
      if (value > 0) return 'positive';
      if (value < 0) return 'negative';
      return '';
    },
    getAverageCapColor(averageValue) {
      return (averageValue <= 100000) ? 'negative' : '';
    },

    getTechnicalArrow(value) {
      if (!value) return '-';
      if (value === 1) return '↑';
      if (value === -1) return '↓';
      return value;
    },
    getHealehTotalScoreClass(score, total) {
      if (score * 2 > total) {
        return 'positive';
      }
      return 'negative';
    },
    getHealthOtherScoreClass(score) {
      return score > 0 ? 'negative' : 'positive';
    },
    getNoticeColorClass(value) {
      if (value > 0) {
        return 'positive';
      }
      else if (value < 0) {
        return 'negative';
      }
      else {
        return '';
      }
  },
    getScoreClass(score) {
      if (!score && score !== 0) return '';
      const num = parseFloat(score);
      if (num >= 80) return 'excellent';
      if (num >= 60) return 'good';
      if (num >= 40) return 'fair';
      return 'poor';
    },

    getRankingColor(pos, total) {
      if (!pos || !total) return '';
      const numPos = parseInt(pos);
      const numTotal = parseInt(total);
      if (numPos < numTotal * 0.2) return 'ranking-green';
      if (numPos < numTotal * 0.5) return '';
      return 'ranking-red';
    },
    getPlanDateColor(dateDiff) {
      return (Math.abs(dateDiff) > 60) ? 'positive' : 'negative'; 
    },

    handleAddToMonitor() {
      if (this.basicData && this.basicData.stockCode) {
        const result = addToMonitor(this.basicData.stockCode);
        if (result.success) {
          // 添加成功后，重新加载监控列表并更新按钮状态
          this.loadMonitoredStocks();
        }
        // 不再显示弹窗提示，只通过按钮状态反馈
      } else {
        // 不再显示弹窗提示
      }
    },
    
    // 从localStorage加载监控列表
    loadMonitoredStocks() {
      try {
        const stocks = localStorage.getItem('monitoredStocks');
        if (stocks) {
          this.monitoredStocks = JSON.parse(stocks);
        } else {
          this.monitoredStocks = [];
        }
      } catch (e) {
        console.error("解析监控列表数据出错:", e);
        this.monitoredStocks = [];
      }
      console.log("monitored stocks: ", this.monitoredStocks);
    },
    
    // 从localStorage加载历史记录
    loadCheckHistory() {
      try {
        const history = localStorage.getItem('checkHistory');
        if (history) {
          // 过滤掉当前股票代码
          this.checkHistory = JSON.parse(history).filter(code => code !== this.realStockCode);
        } else {
          this.checkHistory = [];
        }
      } catch (e) {
        console.error("解析历史记录数据出错:", e);
        this.checkHistory = [];
      }
      console.log("load check history: ", this.checkHistory, "current: ", this.realStockCode);
    },
    
    // 添加到历史记录
    addToCheckHistory(stockCode) {
      const history = localStorage.getItem('checkHistory');
      console.log("addToCheckHistory, adding ", stockCode, " to ", history);
      
      // 初始化历史记录数组
      let checkHistory = [];
      
      // 如果历史记录存在且不为空，则解析它
      if (history && history !== 'null' && history !== 'undefined' && history !== '') {
        try {
          checkHistory = JSON.parse(history);
        } catch (e) {
          console.error("解析历史记录出错:", e);
          checkHistory = []; // 解析失败时使用空数组
        }
      }
      
      // 确保checkHistory是数组
      if (!Array.isArray(checkHistory)) {
        checkHistory = [];
      }
      
      // 移除已存在的相同代码
      checkHistory = checkHistory.filter(code => code !== stockCode);
      
      // 添加到开头
      checkHistory.unshift(stockCode);
      
      // 保持最多6个记录
      if (checkHistory.length > 6) {
        checkHistory = checkHistory.slice(0, 6);
      }
      
      // 保存到localStorage
      try {
        console.log("saving history: ", checkHistory);
        localStorage.setItem('checkHistory', JSON.stringify(checkHistory));
      } catch (e) {
        console.error("保存历史记录出错:", e);
      }
      this.loadCheckHistory();
    },
    // 点击历史记录项重新检查股票
    checkHistoricalStock(stockCode) {
      // 直接重定向到HealthCheck页面并带上stockCode参数
      this.$router.push({
        name: 'HealthCheck',
        params: { stockCode: stockCode }
      }).catch(err => {
        // 忽略重复导航的错误
        if (err.name !== 'NavigationDuplicated') {
          console.error('Routing error:', err);
        }
      });
    },
    
    // 检查股票是否已在监控列表中
    isStockMonitored() {
      if (!this.basicData || !this.basicData.stockCode) {
        return false;
      }
      const fullStockCode = uniformStockCode(this.basicData.stockCode);
      return this.monitoredStocks.some(stock => stock.code === fullStockCode);
    },
    
    getCurrentTimeFormatted() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `体检时间：${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    getDateDiff(notice) {
      if (!notice || !notice.notice_date) {
        return "距今：0月零0天";
      }
      
      // 解析 notice_date 字符串为日期对象
      const noticeDate = new Date(notice.notice_date);
      
      // 获取今天的日期
      const today = new Date();
      
      // 设置时间为00:00:00，避免时间部分影响天数计算
      noticeDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      // 判断noticeDate是否大于今天
      if (noticeDate > today) {
        // noticeDate大于今天，计算还有多少时间
        let months = 0;
        let tempDate = new Date(today);
        
        // 计算月份数
        while (tempDate < noticeDate) {
          const nextMonth = new Date(tempDate);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          
          if (nextMonth <= noticeDate) {
            months++;
            tempDate = nextMonth;
          } else {
            break;
          }
        }
        
        // 计算剩余天数
        const remainingDays = Math.round((noticeDate.getTime() - tempDate.getTime()) / (1000 * 3600 * 24));
        
        return `还有：${months}月零${remainingDays}天`;
      } else {
        // noticeDate小于等于今天，计算距今多长时间
        let months = 0;
        let tempDate = new Date(noticeDate);
        
        // 计算月份数
        while (tempDate < today) {
          const nextMonth = new Date(tempDate);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          
          if (nextMonth <= today) {
            months++;
            tempDate = nextMonth;
          } else {
            break;
          }
        }
        
        // 计算剩余天数
        const remainingDays = Math.round((today.getTime() - tempDate.getTime()) / (1000 * 3600 * 24));
        
        return `距今：${months}月零${remainingDays}天`;
      }
    },
    getFinanceDateDiffColorClass(notice) {
      if (!notice || !notice.notice_date) {
        return '';
      }
      
      // 解析 notice_date 字符串为日期对象
      const noticeDate = new Date(notice.notice_date);
      
      // 获取今天的日期
      const today = new Date();
      
      // 设置时间为00:00:00，避免时间部分影响天数计算
      noticeDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      // 计算两个日期之间的毫秒差
      const timeDiff = noticeDate.getTime() - today.getTime();
      
      // 将毫秒差转换为天数差（取绝对值）
      const daysDiff = Math.abs(Math.ceil(timeDiff / (1000 * 3600 * 24)));
      
      // 根据天数差返回相应的CSS类名
      if (daysDiff <= 10) {
        return 'negative';
      } else if (daysDiff > 30) {
        return 'positive';
      } else {
        return '';
      }
    },

    formatNumber,
    formatFinancialNumber,
    getPricePrecision    
  }
};
</script>

<style scoped>
/* 基础布局样式 */
.page-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h2 {
  color: #2c3e50;
  font-size: 2em;
  margin: 0;
}

/* 搜索区域样式 */
.search-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.search-form {
  display: flex;
  gap: 10px;
  max-width: 400px;
  margin: 0 auto;
}

.stock-input {
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.check-btn {
  padding: 12px 24px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.check-btn:hover:not(:disabled) {
  background-color: #359268;
}

.check-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.history-section {
  margin-top: 15px;
  text-align: center;
}

.history-label {
  color: #666;
  margin-right: 10px;
  vertical-align: middle;
}

.history-list {
  display: inline-flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  vertical-align: middle;
}

.history-item {
  color: #81c9e0; /* 淡蓝色 */
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
}

.history-item:hover {
  text-decoration: underline;
  color: #47bceb; /* 鼠标悬停时稍微深一点的淡蓝色 */
}

/* 股票基本信息样式 */
.stock-basic-info {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.basic-info-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.left-group {
  display: flex;
  align-items: center;
  gap: 20px;
}

.stock-name {
  font-size: 1.5em;
  font-weight: bold;
  margin-right: 30px;
}
.stock-code-text {
  font-size: 0.8em;
  color: #666;
}

.price-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.add-to-monitor-btn {
  padding: 8px 16px;
  background-color: #add8e6; /* 淡蓝色 */
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  margin-left: 20px;
}

.add-to-monitor-btn:hover {
  background-color: #87ceeb; /* 鼠标悬停时稍微深一点的淡蓝色 */
}

.add-to-monitor-btn:disabled {
  background-color: #e0e0e0; /* 淡灰色 */
  color: #999999;
  cursor: not-allowed;
}

.price {
  font-size: 1.2em;
  font-weight: bold;
}

.change {
  font-size: 1.2em;
}

.time-info {
  color: #666;
  font-size: 0.9em;
  text-align: right;
}

/* 各数据部分通用样式 */
.section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.section h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  border-bottom: 2px solid #42b983;
  padding-bottom: 10px;
}
.section-score {
  margin-left: 50px;
}

.fundamentals-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.metric-card {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
}

.metric-card h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  text-align: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 8px;
}

.metric-item {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
  padding: 5px 0;
}

.label {
  color: #666;
  font-weight: 500;
  width: 120px;
  flex-shrink: 0;
}

.value {
  font-weight: bold;
  color: #2c3e50;
  text-align: left;
}

.value {
  font-weight: bold;
  color: #2c3e50;
}

/* 得分样式 */
.score-value {
  font-size: 1.2em;
  font-weight: bold;
  padding: 5px 10px;
  border-radius: 4px;
}

.score-value.large {
  font-size: 1.8em;
}

.score-value.excellent {
  color: #27ae60;
  background-color: #d5f4e6;
}

.score-value.good {
  color: #f39c12;
  background-color: #fef9e7;
}

.score-value.fair {
  color: #e67e22;
  background-color: #fdebd0;
}

.score-value.poor {
  color: #e74c3c;
  background-color: #fadbd8;
}

/* 技术指标特殊样式 */
.technical-section .metric-card {
  background: #e8f4f8;
  text-align: center;
}

/* Technical table styles */
.technical-table-container {
  overflow-x: auto;
}

.technical-table {
  width: 100%;
  border-collapse: collapse;
  background: #f8f9fa;
  border-radius: 6px;
  overflow: hidden;
}

.technical-table th,
.technical-table td {
  padding: 12px 15px;
  text-align: center;
  border-bottom: 1px solid #ddd;
}

/* 体检页特殊定义：负面的用红色警示 */
.negative {
  color: rgb(217,1,28) !important;
}
.positive {
  color: rgb(81,131,2) !important;
}
.ranking-green {
  color: rgb(81,131,2);
}
.ranking-red {
  color: rgb(217,1,28);
}
.technical-table th {
  background-color: #e8f4f8;
  font-weight: bold;
  color: #2c3e50;
}

.technical-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}

.technical-table tbody tr:hover {
  background-color: #f2f8fb;
}

.technical-table .score-value {
  font-size: 1em;
  padding: 3px 8px;
  border-radius: 3px;
}

/* 等待中占位符样式 */
.loading-placeholder {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

/* 错误消息样式 */
.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
  text-align: center;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-form {
    flex-direction: column;
  }
  
  .fundamentals-grid {
    grid-template-columns: 1fr;
  }
  
  .price-info {
    flex-direction: column;
    gap: 10px;
  }
  
  .basic-info-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .left-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .stock-name {
    text-align: center;
  }
  
  .time-info {
    text-align: center;
  }
  
  .price {
    font-size: 1.5em;
  }
  
  .change {
    font-size: 1.2em;
  }
  
  /* Technical table responsive */
  .technical-table {
    font-size: 0.9em;
  }
  
  .technical-table th,
  .technical-table td {
    padding: 8px 10px;
  }
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  font-weight: bold;
  color: #2c3e50;
}

/* 其他信息部分样式 */
.stock-plan-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stock-notice-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stock-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.stock-notice:last-child {
  border-bottom: none;
}

.notice-date {
  width: 100px;
  text-align: left;
  flex-shrink: 0;
  font-weight: bold;
  color: #2c3e50;
}

.notice-title {
  text-align: left;
  width: 700px;
  flex-shrink: 0;
  word-wrap: break-word;
}

.notice-datediff {
  width: 130px;
  text-align: left;
  flex-shrink: 0;
  font-weight: bold;
}

.notice-link {
  color: #81c9e0; /* 柔和的蓝色 */
  text-decoration: none;
  margin-right: 10px;
}

.notice-link:hover {
  text-decoration: underline;
  color: #47bceb; /* 悬停时稍微深一点的柔和蓝色 */
}

.stock-notice span {
  color: #666;
  font-size: 0.9em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stock-plan {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.stock-plan:last-child {
  border-bottom: none;
}

.stock-plan-name, .stock-plan-date {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stock-plan-name .label {
  width: 180px;
}
.stock-plan-name .value {
  width: 240px;
}
.stock-plan-date {
  width: 220px;
}
.stock-plan-date .label {
  width: 50px;
}
.stock-plan-notice {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  text-align: left;
}
.notice-date {
  width: 100px;
}
.notice-title {
  text-align: left;
  width: 700px;
}

.score-value {
  font-size: 1.2em;
  font-weight: bold;
  padding: 5px 10px;
  border-radius: 4px;
}

.score-value.excellent {
  color: #27ae60;
  background-color: #d5f4e6;
}

.score-value.good {
  color: #f39c12;
  background-color: #fef9e7;
}

.score-value.fair {
  color: #e67e22;
  background-color: #fdebd0;
}

.score-value.poor {
  color: #e74c3c;
  background-color: #fadbd8;
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
  text-align: center;
}

@media (max-width: 768px) {
  .search-form {
    flex-direction: column;
  }
  
  /* 移动端stock-plan样式调整 */
  .stock-plan {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .stock-plan-name, .stock-plan-date {
    width: 100%;
    justify-content: space-between;
  }
}
</style>